import { getDevice } from 'dss-device';
import { deepGet, deepGetOfType } from 'bam-deep-get';

/**
 * Class listens for trackable events and fires custom event to allow for easy use by integrated
 *  Analytics platforms
 */
export default class AnalyticsManager {
    constructor(skdPayload) {
        const { UserActivity } = skdPayload;
        this.triggerEvent = this.triggerEvent.bind(this);
        this.deviceData = {};
        this.userData = {};
        this.userActivityApi = UserActivity.UserActivityApi;
        this.glimpseEvents = UserActivity.GlimpseEvents;
        this.glimpseEvent = UserActivity.GlimpseEvent;

        this.initContextData(skdPayload.sessionInfo);
        this.bindPageLoad();
        this.bindElementEvents();
    }

    /**
     * Getter returns the gloabl context data
     * @returns {object}
     */
    get contextData() {
        return {
            ...this.deviceData,
            ...this.userData,
            ...this.updateDynamicData()
        };
    }

    get sessionInfo() {
        return this._sessionInfo;
    }

    /**
     * Initialize the static context data
     * @param {object} sessionInfo - The current session info from the bam-browser-sdk-sdkSession-util module
     */
    initContextData(sessionInfo) {
        const device = getDevice();
        const { s = {
            visitor: '',
            getNewRepeat: () => {}
        }, s_account, navigator, server_path, visitor } = window;
        const { browser, browserVersion, formFactor, platform, platformVersion, userAgent } = device;
        const deviceStr = (formFactor === 'phone') || (formFactor === 'tablet') ? 'Mobile Web' : 'Web';

        this._sessionInfo = sessionInfo;

        this.deviceData = {
            browserType: browser,
            browserVersion,
            connectionType: deepGet(navigator.connection, 'type', 'unknown'),
            device: deviceStr,
            deviceType: formFactor,
            mid: (visitor) ? visitor.getMarketingCloudVisitorID() : '',
            osName: platform,
            osVersion: platformVersion,
            platform: 'Web',
            platformType: 'Web',
            userAgent
        };

        this.userData = {
            adobeRsid: s_account,
            adobeRsidName: server_path.adobe.rsidname,
            adobeSdkVersion: s.version,
            deviceLanguage: navigator.language.split('-')[0],
            deviceLocale: navigator.language,
            dssDeviceId: (sessionInfo) ? sessionInfo.device.id : '',
            dssSessionId: (sessionInfo) ? sessionInfo.id : '',
            userVisitorState: s.getNewRepeat()
        };
    }

    /**
     * Some analyics data changes per event tracked.
     */
    updateDynamicData() {
        const { s } = window;
        const { screenOrientation } = getDevice();
        const cid = s ? s.Util.getQueryParam('cid') : '';
        const partnerId = s ? s.Util.getQueryParam('partnerId') : '';
        const testId = s ? s.Util.getQueryParam('testId') : '';
        const dssAccountId = deepGet(this._sessionInfo, 'account.id', null);
        const dssProfileId = deepGet(this._sessionInfo, 'profile,id', null);
        const isEU = deepGetOfType(window, 'dssMlpConfigs.regionConfig.isEU', Boolean);

        let consentStatus = '1,2,3,4';

        if (window.OptanonActiveGroups) {
            consentStatus = window.OptanonActiveGroups.substring(1).substring(0, window.OptanonActiveGroups.length - 2);
        } else if (isEU) {
            consentStatus = '1,2';
        }

        let optimizelyContextData;

        if (window.optimizelyData) {
            optimizelyContextData = {
                optimizelyCampaignId: deepGet(window.optimizelyData, 'data.decision.campaignId'),
                optimizelyExperimentId: deepGet(window.optimizelyData, 'data.decision.experimentId'),
                optimizelyVariationId: deepGet(window.optimizelyData, 'data.decision.variationId')
            };
        }

        return {
            cid,
            dssAccountId,
            dssProfileId,
            loggedIn: dssAccountId ? 'true' : 'false',
            previousUrl: document.referrer,
            consentStatus,
            partnerId,
            screenOrientation,
            testId,
            timestamp: new Date().toString(),
            ...optimizelyContextData
        };
    }

    /**
     * Find all elements that are to be tracked and listen for the specified event. Once that event is triggered
     * fire a custom event.
     */
    bindElementEvents() {
        document.querySelectorAll('[data-analytics]').forEach(el => {
            const captureEvent = el.getAttribute('data-analytics') || 'click';

            el.addEventListener(captureEvent, this.triggerEvent);
        });
    }

    /**
     * Listen for the page load event
     */
    bindPageLoad() {
        document.body.addEventListener('load', this.triggerEvent);
    }

    /**
     * Fire a custom event with the string syntax of 'analtyics_$type'
     * @example analytics_click, analytics_load, analytics_change, etc
     * @param {Event} e - Event Object
     */
    triggerEvent(e) {
        let customEvent;

        //IE 11 has CustomEvent Obj but doesn't like the constructor for IE reasons
        try {
            customEvent = new CustomEvent(`analytics_${e.type}`, {
                bubbles: true,
                detail: e.detail
            });
        } catch (err) {
            customEvent = document.createEvent('CustomEvent');
            customEvent.initCustomEvent(`analytics_${e.type}`, true, false, e.detail);
        }

        e.currentTarget.dispatchEvent(customEvent);
    }

    /** Fire the page load even */
    firePageLoad() {
        this.triggerEvent({ type: 'load', currentTarget: document.body });
        this.fireGlimpseEvent('viewLoaded', { pagename: 'D+ Marketing Landing Page' });
    }

    fireAction(action) {
        this.triggerEvent({ type: 'event', currentTarget: document.body, detail: {
            action
        } });
    }

    /** Handle Glimpse Tracking Events **/
    fireGlimpseEvent(glimpseEvent, attributes) {
        const {
            appLaunched,
            viewLoaded
        } = this.glimpseEvents;

        const isEU = deepGetOfType(window, 'dssMlpConfigs.regionConfig.isEU', Boolean);
        const glimpseTimeStamp = new Date();
        glimpseTimeStamp.toISOString();

        let consentStatus = '1,2,3,4';

        if (window.OptanonActiveGroups) {
            consentStatus = window.OptanonActiveGroups.substring(1).substring(0, window.OptanonActiveGroups.length - 2);
        } else if (isEU) {
            consentStatus = '1,2';
        }

        const glimpseTrackingParameters = {
            adobeId: this.deviceData.mid,
            appName: 'Disney+ MLP',
            appVersion: '0.0.0',
            consentFallback: (!window.Optanon),
            consentStatus,
            timeStamp: glimpseTimeStamp
        };

        let event;
        let additionalParameters;

        switch (glimpseEvent) {
            case 'appLaunched':
                event = appLaunched;
                break;
            case 'consentGranted':
                event = new this.glimpseEvent('urn:dss:event:fed:lifecycle:consent:granted');
                additionalParameters = {
                    action: 'Click'
                };
                break;
            case 'viewLoaded':
                event = viewLoaded;
                additionalParameters = {
                    loadType: 'Full',
                    pageName: attributes.pagename
                };
                break;
        }

        this.userActivityApi.setTrackingParameters(glimpseTrackingParameters);
        this.userActivityApi.trackEvent(event, additionalParameters);

    }
}
